import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { CheckCircle, Loader } from 'lucide-react';
import SEO from './SEO';
import { supabase } from '../config/supabase';

type Preview = {
  success?: boolean;
  display_name?: string;
  accepted?: boolean;
  error?: string;
};

type Accepted = {
  success?: boolean;
  display_name?: string;
  partner_code?: string;
  tracked_path?: string;
  error?: string;
};

const PartnerInvite: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [preview, setPreview] = useState<Preview | null>(null);
  const [accepted, setAccepted] = useState<Accepted | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token || token.length < 12) {
        setPreview({ success: false, error: 'invalid' });
        setLoading(false);
        return;
      }
      const { data, error } = await supabase.rpc('preview_partner_invite', { p_token: token });
      if (!cancelled) {
        setPreview(error ? { success: false, error: 'unavailable' } : data);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const accept = async () => {
    if (!token || working) return;
    setWorking(true);
    const { data, error } = await supabase.rpc('accept_partner_invite', { p_token: token });
    setAccepted(error ? { success: false, error: 'unavailable' } : data);
    setWorking(false);
  };

  const trackedUrl = accepted?.tracked_path
    ? `https://www.clubiq.golf${accepted.tracked_path}`
    : '';

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <SEO title="ClubIQ partner invite" description="Free ClubIQ access and a tracked link for instructors and creators." />
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-8">
        {loading && (
          <div className="flex justify-center py-12">
            <Loader className="animate-spin text-green-700" />
          </div>
        )}

        {!loading && preview?.error && (
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Invite not found</h1>
            <p className="text-gray-600 mb-6">This partner invite is invalid or no longer active.</p>
            <Link to="/" className="text-green-700 font-medium">Go to ClubIQ</Link>
          </div>
        )}

        {!loading && preview?.success && !accepted?.success && (
          <div>
            <p className="text-sm uppercase tracking-wide text-green-700 font-semibold mb-2">Partner invite</p>
            <h1 className="text-2xl font-bold text-gray-900 mb-3">
              {preview.display_name}
            </h1>
            <p className="text-gray-700 mb-4">
              ClubIQ already has real golfers using personalized distances and shot advice.
              You are invited to use the full product free, receive a unique tracked link, and give candid feedback.
            </p>
            <p className="text-gray-700 mb-6">
              There is no fee, no commission, and no obligation to share ClubIQ with anyone.
            </p>
            {preview.accepted ? (
              <button
                type="button"
                onClick={accept}
                className="w-full bg-green-700 text-white rounded-xl py-3 font-semibold"
              >
                Show my tracked link
              </button>
            ) : (
              <button
                type="button"
                onClick={accept}
                disabled={working}
                className="w-full bg-green-700 text-white rounded-xl py-3 font-semibold disabled:opacity-60"
              >
                {working ? 'Saving…' : 'Accept free access'}
              </button>
            )}
          </div>
        )}

        {accepted?.success && (
          <div>
            <CheckCircle className="text-green-700 mb-3" />
            <h1 className="text-2xl font-bold text-gray-900 mb-3">You are in</h1>
            <p className="text-gray-700 mb-2">
              Code: <span className="font-mono font-semibold">{accepted.partner_code}</span>
            </p>
            <p className="text-gray-700 mb-4 break-all">
              Tracked link: {trackedUrl}
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Share only if it is useful. Golfers who sign up through this link are attributed to you. ClubIQ will not show you their private profile data.
            </p>
            <Link to="/" className="text-green-700 font-medium">Open ClubIQ</Link>
          </div>
        )}

        {accepted?.error && (
          <p className="text-red-700 mt-4">Could not accept this invite. Try again later.</p>
        )}
      </div>
    </div>
  );
};

export default PartnerInvite;
